/**
 * "Where is it?" without typing coordinates: Here (my location) · On the map (tap it) · At a place.
 * Used for places, a static camera's spot, and a camera run by someone without the app.
 *
 * "On the map" opens a full-screen map (its own small MissionMap) — the same in Plan and Live, so the
 * forms never depend on which page they are on. The form hides its sheet while the map is up
 * (`useMapPick` returns the overlay to render instead).
 */
import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { LatLon, MissionBundle } from '@apos/shared';
import { Button, Icon, Select } from '../../components/ui';
import { MissionMap } from '../../components/map/MissionMap';
import { LOCATION_KIND_LABEL } from '../../app/labels';
import { getPhoneFix } from './phoneFix';

/** Full-screen "tap where it is" map. */
export function MapPicker({ bundle, prompt, onPick, onCancel }: { bundle: MissionBundle; prompt: string; onPick(p: LatLon): void; onCancel(): void }) {
  return createPortal(
    <div className="mappick" role="dialog" aria-modal="true" aria-label={prompt} data-testid="map-picker">
      <MissionMap bundle={bundle} world={null} selection={null} onSelect={() => undefined} showRangeRings={false} interactive={{ onMapClick: onPick }} />
      <div className="mappick__bar" role="status">
        <Icon name="crosshair" size={16} />
        <span>{prompt}</span>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>,
    document.body,
  );
}

/** Map-pick state for a form: `start(prompt, cb)`; render `overlay` instead of the form while set. */
export function useMapPick(bundle: MissionBundle) {
  const [pick, setPick] = useState<{ prompt: string; cb(p: LatLon): void } | null>(null);
  const overlay: ReactNode = pick ? (
    <MapPicker
      bundle={bundle}
      prompt={pick.prompt}
      onCancel={() => setPick(null)}
      onPick={(p) => {
        setPick(null);
        pick.cb(p);
      }}
    />
  ) : null;
  return { overlay, start: (prompt: string, cb: (p: LatLon) => void) => setPick({ prompt, cb }) };
}

export interface WherePickerProps {
  bundle: MissionBundle;
  value: LatLon | null;
  /** `locationId` is set when the spot is one of the mission's places ("At a place"), else null. */
  onChange(p: LatLon, how: { locationId: string | null }): void;
  /** Opens the full-screen map (from useMapPick). */
  onPickOnMap(): void;
  /** Hide "Here (my location)" (e.g. the static-camera aim already offers "Use my phone here"). */
  hideHere?: boolean;
  /** Hide the "where it is now" line (the caller shows it). */
  hideValue?: boolean;
  /** Offer the mission's places ("At a place"). */
  places?: boolean;
  disabled?: boolean;
  label?: string;
}

export function WherePicker({ bundle, value, onChange, onPickOnMap, hideHere, hideValue, places = true, disabled, label = 'Where' }: WherePickerProps) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fix, setFix] = useState<(LatLon & { accuracyM: number | null }) | null>(null);
  // shown with the value: the place it is at, or the GPS accuracy when it came from "Here"
  const placeName = value ? (bundle.locations.find((l) => l.lat === value.lat && l.lon === value.lon)?.name ?? null) : null;
  const acc = value && fix && fix.lat === value.lat && fix.lon === value.lon ? fix.accuracyM : null;
  const here = async () => {
    setErr(null);
    setBusy(true);
    try {
      const f = await getPhoneFix();
      setFix(f);
      onChange({ lat: f.lat, lon: f.lon }, { locationId: null });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const locs = places ? bundle.locations : [];
  return (
    <div className="where" role="group" aria-label={label}>
      <span className="field__label">{label}</span>
      <div className="where__btns">
        {!hideHere ? (
          <Button icon="location" onClick={() => void here()} loading={busy} disabled={disabled}>
            Here (my location)
          </Button>
        ) : null}
        <Button icon="map" onClick={onPickOnMap} disabled={disabled}>
          On the map
        </Button>
        {locs.length ? (
          <Select
            aria-label="At a place"
            value=""
            disabled={disabled}
            className="where__place"
            onChange={(e) => {
              const l = bundle.locations.find((x) => x.id === e.target.value);
              if (!l) return;
              onChange({ lat: l.lat, lon: l.lon }, { locationId: l.id });
            }}
          >
            <option value="">At a place…</option>
            {locs.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} · {LOCATION_KIND_LABEL[l.kind]}
              </option>
            ))}
          </Select>
        ) : null}
      </div>
      {hideValue ? null : (
      <div className="where__value num" data-testid="where-value">
        {value ? (
          <>
            <Icon name="pin" size={14} /> {placeName ? `${placeName} · ` : ''}
            {value.lat.toFixed(5)}, {value.lon.toFixed(5)}
            {acc != null ? ` ±${Math.round(acc)} m` : ''}
          </>
        ) : (
          <span className="muted">Not set yet</span>
        )}
      </div>
      )}
      {err ? (
        <div className="form-error" role="alert">
          {err}
        </div>
      ) : null}
    </div>
  );
}
